import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { shopify, settings as settingsApi } from '../services/api';
import {
  LayoutDashboard, Package, RotateCcw, RefreshCw,
  Receipt, TrendingUp, FileDown, Settings, LogOut,
  Clock, BarChart3, ClipboardList, Truck, FileText, FilePlus2, PieChart, X, ArrowLeftRight,
  ShoppingCart, Link2, Users, PackagePlus, FileSearch, Wallet, Lock, ShieldAlert
} from 'lucide-react';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  perm?: string;
  adminOnly?: boolean;
  secret?: boolean;
}

const PL_NAV_HIDDEN_KEY = 'fs_pl_nav_hidden';

const navSections: { label: string; items: NavItem[] }[] = [
  {
    label: 'Overview',
    items: [
      { path: '/', label: 'Dashboard', icon: LayoutDashboard, perm: 'dashboard' },
      { path: '/analytics', label: 'Analytics', icon: BarChart3, perm: 'analytics' },
      { path: '/orders', label: 'Orders', icon: ClipboardList, perm: 'orders' },
      { path: '/dispatch', label: 'Dispatch', icon: Truck, perm: 'dispatch' },
    ]
  },
  {
    label: 'Operations',
    items: [
      { path: '/inventory', label: 'Inventory', icon: Package, perm: 'inventory' },
      { path: '/inventory-analytics', label: 'Stock Analytics', icon: PieChart, perm: 'inventory_analytics' },
      { path: '/reorder', label: 'Reorder', icon: ShoppingCart, perm: 'reorder' },
      { path: '/rto', label: 'RTO Stock', icon: RotateCcw, perm: 'rto' },
      { path: '/customer-risk', label: 'COD Risk', icon: ShieldAlert, perm: 'customer_risk' },
      { path: '/borrowed-stock', label: 'Borrowed Stock', icon: ArrowLeftRight, perm: 'inventory' },
      { path: '/design-mappings', label: 'Design Mappings', icon: Link2, perm: 'design_mappings' },
      { path: '/blank-material-check', label: 'Blank Material Check', icon: FileSearch, perm: 'reconciliation' },
    ]
  },
  {
    label: 'Finance',
    items: [
      { path: '/expenses', label: 'Expenses', icon: Receipt, perm: 'expenses' },
      { path: '/cash-flow', label: 'Cash Flow', icon: Wallet, perm: 'cashflow' },
      { path: '/pl', label: 'P&L Report', icon: TrendingUp, perm: 'pl', secret: true },
      { path: '/pl-excel-check', label: 'P&L (Excel Check)', icon: FileSearch, perm: 'pl', secret: true },
      { path: '/tax', label: 'Tax Export', icon: FileDown, perm: 'tax' },
      { path: '/invoices', label: 'Invoices', icon: FileText, perm: 'invoices' },
      { path: '/proforma', label: 'Proforma', icon: FilePlus2, perm: 'proforma' },
      { path: '/purchase-receives', label: 'Purchase Receives', icon: PackagePlus, perm: 'proforma' },
    ]
  },
  {
    label: 'System',
    items: [
      { path: '/settings', label: 'Settings', icon: Settings, perm: 'settings' },
      { path: '/users', label: 'Users', icon: Users, perm: 'settings' },
    ]
  },
  {
    label: 'Private',
    items: [
      { path: '/vault', label: 'Banking Vault', icon: Lock, adminOnly: true },
    ]
  },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, hasPermission, isAdmin } = useAuth();
  const { theme } = useTheme();
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [logoDark, setLogoDark] = useState<string | null>(null);
  const [logoLight, setLogoLight] = useState<string | null>(null);
  const [plNavHidden, setPlNavHidden] = useState(() => {
    try { return localStorage.getItem(PL_NAV_HIDDEN_KEY) === 'true'; } catch { return false; }
  });
  const logoClickCountRef = useRef(0);
  const logoClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLogoClick = () => {
    logoClickCountRef.current += 1;
    if (logoClickTimerRef.current) clearTimeout(logoClickTimerRef.current);
    if (logoClickCountRef.current >= 3) {
      setPlNavHidden(prev => {
        const next = !prev;
        try { localStorage.setItem(PL_NAV_HIDDEN_KEY, String(next)); } catch {}
        return next;
      });
      logoClickCountRef.current = 0;
      return;
    }
    logoClickTimerRef.current = setTimeout(() => { logoClickCountRef.current = 0; }, 600);
  };

  useEffect(() => {
    const fetchStatus = () => {
      shopify.getSyncStatus().then(r => setSyncStatus(r.data)).catch(() => {});
    };
    fetchStatus();
    settingsApi.getAll().then(r => {
      setLogoDark(r.data.company_logo_dark || r.data.company_logo || null);
      setLogoLight(r.data.company_logo_light || null);
    }).catch(() => {});
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const currentLogo = theme === 'light' ? (logoLight || logoDark) : logoDark;

  const formatLastSync = (iso: string | null) => {
    if (!iso) return 'Never';
    const d = new Date(iso + 'Z');
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    return `${Math.floor(diff / 60)}h ago`;
  };

  const handleNav = (path: string) => {
    navigate(path);
    if (onClose) onClose();
  };

  const visibleSections = navSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (item.secret && plNavHidden) return false;
        return item.adminOnly ? isAdmin : hasPermission(item.perm as string);
      }),
    }))
    .filter(section => section.items.length > 0);

  return (
    <div className={`sidebar ${isOpen ? 'open' : ''}`} style={{
      width: 260, background: 'var(--bg-secondary)', borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', height: '100vh', position: 'fixed', zIndex: 50,
    }}>
      <div style={{ padding: '24px 20px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {currentLogo ? (
          <div onClick={handleLogoClick}>
            <img src={currentLogo} alt="Futuresaints" style={{ height: 28, objectFit: 'contain', display: 'block' }} />
            <div style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: 3, textTransform: 'uppercase', marginTop: 6, marginLeft: 2 }}>Operations</div>
          </div>
        ) : (
          <div onClick={handleLogoClick}>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: -0.5, lineHeight: 1.2 }}>FUTURESAINTS</div>
            <div style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: 3, textTransform: 'uppercase', marginTop: 4 }}>Operations</div>
          </div>
        )}
        {onClose && (
          <button className="show-mobile" onClick={onClose} style={{
            background: 'var(--bg-elevated)', border: 'none', borderRadius: 8,
            width: 30, height: 30, display: 'none', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}><X size={15} color="var(--text-muted)" /></button>
        )}
      </div>

      <div style={{ flex: 1, padding: '12px 12px', overflowY: 'auto' }}>
        {visibleSections.map((section) => (
          <div key={section.label} style={{ marginBottom: 4 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 1.5, textTransform: 'uppercase', padding: '14px 14px 8px' }}>{section.label}</div>
            {section.items.map(item => {
              const active = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <div key={item.path} onClick={() => handleNav(item.path)} style={{
                  display: 'flex', alignItems: 'center', gap: 11, padding: '10px 14px',
                  borderRadius: 'var(--radius-sm)', cursor: 'pointer', marginBottom: 1,
                  color: active ? 'var(--accent-blue)' : 'var(--text-secondary)',
                  background: active ? 'var(--accent-blue-soft)' : 'transparent',
                  fontWeight: active ? 600 : 400, fontSize: 13.5,
                  transition: 'var(--transition)', position: 'relative',
                }}
                  onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)'; }}
                  onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  {active && <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: '55%', background: 'var(--accent-blue)', borderRadius: '0 4px 4px 0' }} />}
                  <Icon size={17} strokeWidth={active ? 2.2 : 1.8} />
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div style={{ padding: '12px 14px', borderTop: '1px solid var(--border)' }}>
        <div style={{
          background: syncStatus?.syncing ? 'linear-gradient(135deg, rgba(79,125,249,0.08), rgba(157,122,255,0.08))' : 'var(--accent-green-soft)',
          border: `1px solid ${syncStatus?.syncing ? 'rgba(79,125,249,0.2)' : 'rgba(45,212,160,0.15)'}`,
          borderRadius: 'var(--radius-md)', padding: '10px 12px', marginBottom: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            {syncStatus?.syncing ? (
              <RefreshCw size={12} color="var(--accent-blue)" style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-green)', boxShadow: '0 0 0 3px rgba(45,212,160,0.2)', animation: 'pulse-dot 2s ease infinite' }} />
            )}
            <span style={{ fontSize: 11.5, fontWeight: 600, color: syncStatus?.syncing ? 'var(--accent-blue)' : 'var(--accent-green)' }}>
              {syncStatus?.syncing ? 'Syncing...' : 'Shopify Connected'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Clock size={9} color="var(--text-muted)" />
            <span style={{ fontSize: 9.5, color: 'var(--text-muted)' }}>Auto-sync · Last: {formatLastSync(syncStatus?.last_sync)}</span>
          </div>
        </div>

        <div style={{ background: 'var(--accent-purple-soft)', border: '1px solid rgba(157,122,255,0.15)', borderRadius: 'var(--radius-md)', padding: '10px 12px', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Truck size={12} color="var(--accent-purple)" />
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--accent-purple)' }}>Delhivery · Webhook</span>
          </div>
        </div>

        <button onClick={logout} style={{
          width: '100%', padding: '10px 14px', background: 'var(--bg-card)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
          color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'var(--transition)',
        }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--accent-red-soft)'; (e.currentTarget as HTMLElement).style.color = 'var(--accent-red)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(239,90,111,0.2)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-card)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; }}
        ><LogOut size={14} /> Sign Out</button>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Sidebar;
